// The forced first-login password change (task #127). Shown by Landing when
// users/{me}.mustChangePassword is true - the account is still on the studio
// temp password, so nothing else renders until they pick their own.

import { useState, type CSSProperties, type FormEvent } from 'react';
import { getAuth, updatePassword } from 'firebase/auth';
// NOT firebase/auth's signOut. This was the ONE sign-out in the app that
// called the SDK directly, and it is the one reached by a staff account still
// on the studio temp password, on whatever device is to hand. The wrapper
// detaches this device's push token, terminates Firestore, wipes IndexedDB and
// reloads; the raw SDK call does none of that.
//
// Proven in a browser on a persistent profile: after the raw sign-out, a tab
// with NOBODY signed in still read a dancer's surname and date of birth out of
// the Firestore cache, which is per ORIGIN and not per user. Through the
// wrapper the same measurement drops from 10 cached documents to 3 and the
// planted canary disappears.
//
// Worse than it sounds, because this screen is a fixed overlay rendered over
// whatever route you are on rather than a gate before the app boots, so the
// page underneath mounts and fills that cache while the person is still
// looking at "pick a new password".
import { signOut } from '../lib/auth';
import { markPasswordChanged } from '../lib/tempAccounts';
import PasswordField from './PasswordField';

const cardStyle: CSSProperties = {
  background: 'var(--white)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 'var(--space-5)',
  display: 'grid',
  gap: 'var(--space-3)',
  maxWidth: 440,
  margin: '0 auto',
};

const inputStyle: CSSProperties = {
  minHeight: 44,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--line)',
  padding: '0 var(--space-3)',
  fontSize: 16,
  width: '100%',
  background: 'var(--white)',
  color: 'var(--ink)',
};

const btnStyle: CSSProperties = {
  minHeight: 48,
  borderRadius: 'var(--radius-md)',
  background: 'var(--lime)',
  border: 'none',
  color: '#0E1A0A',
  fontWeight: 900,
  fontSize: 15,
  cursor: 'pointer',
};

export default function ForcePasswordChange({ onDone }: { onDone?: () => void }) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    // Trim before we validate or save. A password typed on a phone
    // keyboard, or pasted from a notes app, can easily carry a leading or
    // trailing space nobody meant to add - and that space would silently
    // become part of their new password, so future sign-ins would fail
    // for the same "invisible mismatch" reason this whole gate exists to
    // fix. Trimming here is never a real part of anyone's password.
    const newPw = pw1.trim();
    const confirmPw = pw2.trim();
    if (newPw.length < 8) {
      setErr('Pick at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setErr('The two passwords do not match.');
      return;
    }
    const me = getAuth().currentUser;
    if (!me) {
      setErr('Your sign-in expired. Refresh and sign in again.');
      return;
    }
    setBusy(true);
    try {
      await updatePassword(me, newPw);
      try {
        await markPasswordChanged();
      } catch {
        // Retry once - a cold serverless function is the common hiccup.
        try {
          await markPasswordChanged();
        } catch {
          // The password DID change. The Continue button below dismisses
          // the gate for this session either way - never trap them - and
          // the claim clears for good on the next successful pass.
        }
      }
      setDone(true);
    } catch (e2) {
      const code = (e2 as { code?: string })?.code || '';
      if (code === 'auth/requires-recent-login') {
        setErr('For safety, sign out and sign back in with the temp password, then set your new one right away.');
      } else if (code === 'auth/weak-password') {
        setErr('That password is too easy to guess. Try a longer one.');
      } else {
        setErr(e2 instanceof Error ? e2.message : 'Could not save the new password. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section style={cardStyle} aria-label="Password updated">
        <div style={{ fontSize: 20, fontWeight: 900 }}>You're all set!</div>
        <div style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
          Your new password is saved. Use it from now on.
        </div>
        <button type="button" style={btnStyle} onClick={() => onDone?.()}>
          Continue to the Hub
        </button>
      </section>
    );
  }

  return (
    <section style={cardStyle} aria-label="Set your own password">
      <div style={{ fontSize: 20, fontWeight: 900 }}>Welcome! Set your own password</div>
      <div style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
        You signed in with the studio's starter password. Pick your own to keep your account
        yours - it takes ten seconds and you only do it once.
      </div>
      <form onSubmit={(e) => void submit(e)} style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 700 }}>
          New password
          <PasswordField
            autoComplete="new-password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 700 }}>
          Type it again
          <PasswordField
            autoComplete="new-password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            style={inputStyle}
          />
        </label>
        {err && <div style={{ fontSize: 13, color: 'var(--rose-deep)' }}>{err}</div>}
        <button type="submit" style={btnStyle} disabled={busy}>
          {busy ? 'Saving...' : 'Save my password'}
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-soft)',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            minHeight: 40,
          }}
        >
          Sign out instead
        </button>
      </form>
    </section>
  );
}
